import './TestDesign.css';
import '../../styles/osPills.css';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronUp, ChevronDown, ChevronRight, Columns3, ChevronsUpDown, Ellipsis, Expand, ExternalLink, FolderOpen, Folder, Layers, ListChecks, ListFilter, Pencil, Plus, Search, Info, Trash2, X, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { FaApple, FaAndroid, FaReact } from 'react-icons/fa';
import { TbDeviceMobileCog } from 'react-icons/tb';
import { FaFlutter } from 'react-icons/fa6';
import { SiApachecordova } from 'react-icons/si';
import { IoLogoCapacitor } from 'react-icons/io5';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import FolderPicker from '../../components/TestCaseDetail/FolderPicker';

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';

function AddFolderControl({ parentNode, onCreateFolder, title }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setSubmitting(false);
    setErr(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      close();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  function openPopover(e) {
    e.stopPropagation();
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setName('');
    setErr(null);
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setErr('Folder name is required.'); return; }
    setSubmitting(true);
    setErr(null);
    try {
      await onCreateFolder(parentNode, trimmed);
      close();
    } catch {
      setErr('Failed to create folder.');
      setSubmitting(false);
    }
  }

  return (
    <>
      <button ref={btnRef} className="tm-folder-add" title={title} onClick={openPopover}>
        <Plus size={12} />
      </button>
      {open && pos && (
        <div
          ref={popRef}
          className="tm-folder-add-popover"
          style={{ top: pos.top, left: pos.left }}
          onClick={e => e.stopPropagation()}
        >
          <form onSubmit={submit}>
            <div className="tm-folder-add-title">
              {parentNode ? `New folder in "${parentNode.name}"` : 'New root folder'}
            </div>
            <input
              className="tm-folder-add-input"
              autoFocus
              placeholder="Folder name"
              value={name}
              onChange={e => setName(e.target.value.replace(/\//g, ''))}
              disabled={submitting}
            />
            {err && <div className="tm-folder-add-error">{err}</div>}
            <div className="tm-folder-add-actions">
              <button type="button" className="tm-folder-add-cancel" onClick={close} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="tm-folder-add-create" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function FolderActionsMenu({ node, onDeleteFolder, onRenameFolder }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formPos, setFormPos] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPos, setConfirmPos] = useState(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const formRef = useRef(null);
  const confirmRef = useRef(null);
  const isDeletable = !node.children?.length && node.testCaseCount === 0;

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setFormOpen(false);
    setConfirmOpen(false);
    setSubmitting(false);
    setErr(null);
  }, []);

  useEffect(() => {
    if (!menuOpen && !formOpen && !confirmOpen) return;
    function onDocMouseDown(e) {
      if (
        btnRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target) ||
        formRef.current?.contains(e.target) ||
        confirmRef.current?.contains(e.target)
      ) return;
      closeAll();
    }
    function onKey(e) { if (e.key === 'Escape') closeAll(); }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, formOpen, confirmOpen, closeAll]);

  function openMenu(e) {
    e.stopPropagation();
    const r = btnRef.current.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left });
    setMenuOpen(true);
  }

  function handleRename(e) {
    e.stopPropagation();
    setMenuOpen(false);
    const r = btnRef.current.getBoundingClientRect();
    setFormPos({ top: r.bottom + 4, left: r.left });
    setName(node.name);
    setErr(null);
    setFormOpen(true);
  }

  function handleDelete(e) {
    e.stopPropagation();
    setMenuOpen(false);
    const r = btnRef.current.getBoundingClientRect();
    setConfirmPos({ top: r.bottom + 4, left: r.left });
    setConfirmOpen(true);
  }

  function confirmDelete(e) {
    e.stopPropagation();
    closeAll();
    onDeleteFolder(node);
  }

  async function submitRename(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setErr('Folder name is required.'); return; }
    if (trimmed === node.name) { closeAll(); return; }
    setSubmitting(true);
    setErr(null);
    try {
      await onRenameFolder(node, trimmed);
      closeAll();
    } catch {
      setErr('Failed to rename folder.');
      setSubmitting(false);
    }
  }

  return (
    <>
      <button ref={btnRef} className="tm-folder-delete" title="Folder actions" onClick={openMenu}>
        <Ellipsis size={12} />
      </button>
      {menuOpen && menuPos && (
        <div
          ref={menuRef}
          className="tm-folder-menu"
          style={{ top: menuPos.top, left: menuPos.left }}
          onClick={e => e.stopPropagation()}
        >
          <button className="tm-folder-menu-item" onClick={handleRename} disabled>
            <Pencil size={13} />
            Rename folder
          </button>
          <button
            className="tm-folder-menu-item"
            onClick={handleDelete}
            disabled={!isDeletable}
            title={isDeletable ? '' : 'Cannot delete: folder has subfolders or test cases'}
          >
            <Trash2 size={13} />
            Delete folder
          </button>
        </div>
      )}
      {formOpen && formPos && (
        <div
          ref={formRef}
          className="tm-folder-add-popover"
          style={{ top: formPos.top, left: formPos.left }}
          onClick={e => e.stopPropagation()}
        >
          <form onSubmit={submitRename}>
            <div className="tm-folder-add-title">Rename "{node.name}"</div>
            <input
              className="tm-folder-add-input"
              autoFocus
              placeholder="Folder name"
              value={name}
              onChange={e => setName(e.target.value.replace(/\//g, ''))}
              disabled={submitting}
            />
            {err && <div className="tm-folder-add-error">{err}</div>}
            <div className="tm-folder-add-actions">
              <button type="button" className="tm-folder-add-cancel" onClick={closeAll} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="tm-folder-add-create" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
      {confirmOpen && confirmPos && (
        <div
          ref={confirmRef}
          className="tm-folder-add-popover"
          style={{ top: confirmPos.top, left: confirmPos.left }}
          onClick={e => e.stopPropagation()}
        >
          <div className="tm-folder-add-title">Delete "{node.name}"?</div>
          <p className="tm-folder-confirm-msg">This action cannot be undone.</p>
          <div className="tm-folder-add-actions">
            <button type="button" className="tm-folder-add-cancel" onClick={closeAll}>
              Cancel
            </button>
            <button type="button" className="tm-folder-delete-confirm" onClick={confirmDelete}>
              Delete
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function collectAncestorIds(node, targetId, acc) {
  if (!node) return false;
  if (node.id === targetId) return true;
  for (const child of node.children ?? []) {
    if (collectAncestorIds(child, targetId, acc)) {
      acc.add(node.id);
      return true;
    }
  }
  return false;
}

function FolderNode({ node, depth, selectedFolder, expandedIds, onSelectFolder, onDeleteFolder, onRenameFolder, onCreateFolder }) {
  const [isExpanded, setIsExpanded] = useState(() => expandedIds?.has(node.id) ?? false);
  const isNodeSelected = selectedFolder?.id === node.id;
  const hasChildren = node.children?.length > 0;

  function handleRowClick() {
    onSelectFolder(node);
    if (hasChildren && !isExpanded) setIsExpanded(true);
  }

  return (
    <div>
      <div className="tm-folder-row">
        <button
          className={`tm-folder-item ${isNodeSelected ? 'active' : ''}`}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
          onClick={handleRowClick}
        >
          {hasChildren ? (
            <span
              className="tm-folder-chevron"
              onClick={e => { e.stopPropagation(); setIsExpanded(exp => !exp); }}
            >
              {isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </span>
          ) : (
            <span className="tm-folder-chevron-spacer" />
          )}
          {isExpanded && hasChildren ? <FolderOpen size={13} /> : <Folder size={13} />}
          <span className="tm-folder-item-name">{node.name}</span>
          <span className="tm-folder-count">{node.testCaseCount}</span>
        </button>
        <AddFolderControl parentNode={node} onCreateFolder={onCreateFolder} title={`Add subfolder in "${node.name}"`} />
        <FolderActionsMenu node={node} onDeleteFolder={onDeleteFolder} onRenameFolder={onRenameFolder} />
      </div>
      {isExpanded && hasChildren && node.children.map(child => (
        <FolderNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedFolder={selectedFolder}
          expandedIds={expandedIds}
          onSelectFolder={onSelectFolder}
          onDeleteFolder={onDeleteFolder}
          onRenameFolder={onRenameFolder}
          onCreateFolder={onCreateFolder}
        />
      ))}
    </div>
  );
}

const COLUMNS = [
  { label: 'Key',               key: 'key' },
  { label: 'Name',              key: 'summary' },
  { label: 'Test Type',         key: 'type' },
  { label: 'Reporter',          key: 'reporter' },
  { label: 'Updated',           key: 'created' },
  { label: 'Automation Status', key: 'automationStatus' },
  { label: 'Labels',            key: 'unit' },
  { label: 'Status',            key: 'status' },
  { label: 'Tech stack',        key: 'testPyramid' },
  { label: 'Folder',            key: 'parentId' },
  { label: 'Mobile Framework',  key: 'mobile_framework' },
  { label: 'Mobile OS',         key: 'mobile_os' },
];

const DISABLED_BY_DEFAULT = new Set(['automationStatus', 'testPyramid', 'parentId']);

const FILTERABLE_FIELDS = [
  { label: 'Status',            key: 'status' },
  { label: 'Test Type',         key: 'type' },
  { label: 'Priority',          key: 'priority' },
  { label: 'Labels / Unit',     key: 'unit' },
  { label: 'Automation Status', key: 'automationStatus' },
  { label: 'Tech Stack',        key: 'testPyramid' },
  { label: 'Reporter',          key: 'reporter' },
  { label: 'Mobile Framework',  key: 'mobile_framework' },
  { label: 'Mobile OS',         key: 'mobile_os' },
];

// Every test-case field the backend allows bulk-updating (QA-7180), grouped by
// the kind of input each category needs. Keys match both the field-options
// response and the test-case row fields.
//  - single: one option value        (<select>)
//  - multi:  a set of option values   (checkbox list)
//  - labels: tags with ADD/REPLACE/REMOVE semantics (BulkLabelsUpdateDto)
//  - text:   a plain string
//  - rich:   a rich-text string (ADF on the backend)
const BULK_FIELD_GROUPS = [
  {
    key: 'single',
    title: 'Single value',
    hint: 'Pick one',
    kind: 'single',
    fields: [
      { label: 'Test Type',         key: 'type' },
      { label: 'Automation Status', key: 'automationStatus' },
      { label: 'Labels / Unit',     key: 'unit' },
      { label: 'Test Pyramid',      key: 'testPyramid' },
      { label: 'Priority',          key: 'priority' },
    ],
  },
  {
    key: 'multi',
    title: 'Multi value',
    hint: 'Pick any; replaces the field',
    kind: 'multi',
    fields: [
      { label: 'Project Type', key: 'projectType' },
      { label: 'Tech Stack',   key: 'techStack' },
    ],
  },
  {
    key: 'labels',
    title: 'Labels (tags)',
    hint: 'Existing project labels only',
    kind: 'labels',
    fields: [
      { label: 'Labels', key: 'labels' },
    ],
  },
  {
    key: 'text',
    title: 'Text',
    hint: 'Replaces the value',
    kind: 'text',
    fields: [
      { label: 'Folder Path', key: 'folderPath' },
    ],
  },
  {
    key: 'rich',
    title: 'Rich text',
    hint: 'Replaces the value',
    kind: 'rich',
    fields: [
      { label: 'Precondition', key: 'precondition' },
    ],
  },
];

// Labels edit modes offered in bulk edit. The backend also supports REMOVE, but
// it's intentionally not exposed here (ADD / REPLACE only).
const LABELS_OPERATIONS = ['ADD', 'REPLACE'];

const OS_CONFIG = {
  ios:     { label: 'iOS',     Icon: FaApple,   className: 'tm-os-pill tm-os-ios' },
  android: { label: 'Android', Icon: FaAndroid, className: 'tm-os-pill tm-os-android' },
};

const FRAMEWORK_CONFIG = {
  'react native': { label: 'React Native', Icon: FaReact,            className: 'tm-fw-pill tm-fw-react-native' },
  'native':       { label: 'Native',       Icon: TbDeviceMobileCog,  className: 'tm-fw-pill tm-fw-native' },
  'flutter':      { label: 'Flutter',      Icon: FaFlutter,          className: 'tm-fw-pill tm-fw-flutter' },
  'cordova':      { label: 'Cordova',      Icon: SiApachecordova,    className: 'tm-fw-pill tm-fw-cordova' },
  'capacitor':    { label: 'Capacitor',    Icon: IoLogoCapacitor,    className: 'tm-fw-pill tm-fw-capacitor' },
};

const MISSING_VALUE = '__missing__';
const Missing = () => <span className="tm-missing"><Info size={12} />missing</span>;

function OsPills({ values }) {
  if (!values?.length) return <Missing />;
  return (
    <span className="tm-os-pills">
      {values.map(v => {
        const cfg = OS_CONFIG[v.toLowerCase()] ?? { label: v, Icon: null, className: 'tm-os-pill tm-os-default' };
        return (
          <span key={v} className={cfg.className}>
            {cfg.Icon && <cfg.Icon size={11} />}
            {cfg.label}
          </span>
        );
      })}
    </span>
  );
}

function FrameworkPills({ values }) {
  const arr = Array.isArray(values) ? values : values ? values.split(',').map(v => v.trim()) : [];
  if (!arr.length) return <Missing />;
  return (
    <span className="tm-fw-pills">
      {arr.map(v => {
        const cfg = FRAMEWORK_CONFIG[v.toLowerCase()] ?? { label: v, Icon: null, className: 'tm-fw-pill tm-fw-default' };
        return (
          <span key={v} className={cfg.className}>
            {cfg.Icon && <cfg.Icon size={11} />}
            {cfg.label}
          </span>
        );
      })}
    </span>
  );
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={12} className="sort-icon inactive" />;
  return sortDir === 'asc'
    ? <ChevronUp size={12} className="sort-icon" />
    : <ChevronDown size={12} className="sort-icon" />;
}


function statusClass(status) {
  if (!status) return 'tm-status-default';
  const s = status.toLowerCase();
  if (s === 'verified') return 'tm-status-verified';
  if (s === 'backlog') return 'tm-status-backlog';
  return 'tm-status-default';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Multi-select dropdown for the bulk-edit panel (Tech Stack / Project Type / Labels).
 * The menu is position:fixed so it overlays the page and isn't clipped by the
 * panel body's overflow:auto (same approach as Campaigns' FilterSelect).
 */
function BulkMultiSelect({ options, selected, onChange, disabled, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  const toggle = v => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const menuHeight = 240;
    const openUpward = window.innerHeight - r.bottom < menuHeight + 8 && r.top > menuHeight;
    setMenuStyle(openUpward
      ? { bottom: window.innerHeight - r.top + 2, left: r.left, width: r.width }
      : { top: r.bottom + 2, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  const label = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]
    : `${selected.length} selected`;

  return (
    <div className="tm-bulk-dd" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`tm-bulk-dd-trigger${selected.length ? ' has-value' : ''}`}
        disabled={disabled}
        onClick={() => { if (disabled) return; if (!open) reposition(); setOpen(o => !o); }}
      >
        <span className="tm-bulk-dd-label">{label}</span>
        {selected.length > 0 && (
          <span
            className="tm-bulk-dd-clear"
            role="button"
            aria-label="Clear selection"
            onClick={e => { e.stopPropagation(); onChange([]); }}
          >×</span>
        )}
        <ChevronDown size={14} className="tm-bulk-dd-arrow" />
      </button>
      {open && (
        <div className="tm-bulk-dd-menu" style={menuStyle}>
          {options.length === 0 && <div className="tm-bulk-dd-empty">No values</div>}
          {options.map(v => (
            <label key={v} className={`tm-bulk-dd-item${selected.includes(v) ? ' checked' : ''}`}>
              <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
              <span>{v}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function TestDesign({ projects, selectedProject, cases, totalApiCases = 0, loading, loadingMore = false, error, folderTree, selectedFolder, recursive, onSelectProject, onSelectFolder, onToggleRecursive, onCaseClick, onDeleteFolder, onRenameFolder, onCreateFolder, onAddTest, onLoadMore, onBulkUpdate }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const expandedIds = useMemo(() => {
    const acc = new Set();
    if (folderTree && selectedFolder?.id != null) {
      collectAncestorIds(folderTree, selectedFolder.id, acc);
    }
    return acc;
  }, [folderTree, selectedFolder?.id]);
  const [search, setSearch] = useState(() => searchParams.get('ds_search') || '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [visibleCols, setVisibleCols] = useState(
    () => new Set(COLUMNS.map(c => c.key).filter(k => !DISABLED_BY_DEFAULT.has(k)))
  );
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [foldersCollapsed, setFoldersCollapsed] = useState(false);
  const pageSize = 100;

  // ---- Bulk update (QA-7098) ----
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [fieldOpts, setFieldOpts] = useState(null); // per-field option lists + labels[] from /field-options
  const [fieldOptsLoading, setFieldOptsLoading] = useState(false);
  const [fieldOptsError, setFieldOptsError] = useState(null);
  const fieldOptsProjectRef = useRef(null); // project the current options were fetched for
  const [bulkValues, setBulkValues] = useState({}); // chosen value per field key
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const selectAllRef = useRef(null);

  const fetchFieldOptions = useCallback(async project => {
    setFieldOptsLoading(true);
    setFieldOptsError(null);
    try {
      const res = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/field-options`, { params: { project } });
      setFieldOpts(res.data);
      fieldOptsProjectRef.current = project;
    } catch {
      setFieldOptsError('Failed to load field options.');
      setFieldOpts(null);
    } finally {
      setFieldOptsLoading(false);
    }
  }, []);

  function enterBulkMode() {
    setBulkMode(true);
    setBulkResult(null);
    // Options are per-project — (re)fetch whenever the project changed since the last fetch.
    if (selectedProject && fieldOptsProjectRef.current !== selectedProject) {
      setFieldOpts(null);
      setBulkValues({});
      fetchFieldOptions(selectedProject);
    }
  }

  function exitBulkMode() {
    setBulkMode(false);
    setSelectedKeys(new Set());
    setBulkValues({});
    setBulkResult(null);
  }

  function toggleKey(key) {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
  const [activeFilters, setActiveFilters] = useState(() => {
    const initial = [];
    FILTERABLE_FIELDS.forEach(({ key }) => {
      const values = searchParams.getAll(`ds_${key}`);
      values.forEach(val => {
        initial.push({ field: key, value: val });
      });
    });
    return initial;
  });

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete('ds_search');
    FILTERABLE_FIELDS.forEach(({ key }) => {
      next.delete(`ds_${key}`);
    });
    if (search) {
      next.set('ds_search', search);
    }
    activeFilters.forEach(({ field, value }) => {
      next.append(`ds_${field}`, value);
    });
    setSearchParams(next, { replace: true });
  }, [search, activeFilters, setSearchParams]);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterMenuStep, setFilterMenuStep] = useState('field');
  const [filterMenuField, setFilterMenuField] = useState(null);
  const filterMenuRef = useRef(null);

  useEffect(() => {
    if (!filterMenuOpen) return;
    function handleClickOutside(e) {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setFilterMenuOpen(false);
        setFilterMenuStep('field');
        setFilterMenuField(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterMenuOpen]);

  function handleAddTest() {
    if (onAddTest) {
      onAddTest();
    } else {
      window.open('https://contentsquare.atlassian.net/jira/for-you', '_blank', 'noopener,noreferrer');
    }
  }

  function handleSort(key) {
    if (sortCol === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  const prevProjectRef = useRef(selectedProject);
  useEffect(() => {
    if (prevProjectRef.current === selectedProject) return;
    prevProjectRef.current = selectedProject;
    setActiveFilters([]);
    // Leave bulk mode and drop the selection; the next bulk-update click refetches
    // options for the newly selected project.
    setBulkMode(false);
    setSelectedKeys(new Set());
    setBulkValues({});
    setBulkResult(null);
  }, [selectedProject]);
  useEffect(() => { setPage(1); }, [activeFilters]);
  useEffect(() => { setPage(1); }, [selectedFolder]);

  const filtered = useMemo(() => {
    if (!search.trim()) return cases;
    const raw = search.trim();
    const urlMatch = raw.match(/\/browse\/([A-Z]+-\d+)/i);
    const keyMatch = raw.match(/^([A-Z]+-)?(\d+)$/i);
    if (urlMatch) {
      const k = urlMatch[1].toLowerCase();
      return cases.filter(tc => (tc.key ?? '').toLowerCase() === k || (tc.summary ?? '').toLowerCase().includes(k));
    }
    if (keyMatch && keyMatch[1]) {
      // full key like MOBILE-15819
      const k = raw.toLowerCase();
      return cases.filter(tc => (tc.key ?? '').toLowerCase() === k || (tc.summary ?? '').toLowerCase().includes(k));
    }
    if (keyMatch && !keyMatch[1]) {
      // bare number like 15819 — match key ending in -<number>
      const num = keyMatch[2];
      return cases.filter(tc =>
        (tc.key ?? '').toLowerCase().endsWith(`-${num}`) ||
        (tc.summary ?? '').toLowerCase().includes(num)
      );
    }
    const q = raw.toLowerCase();
    return cases.filter(tc =>
      (tc.summary ?? '').toLowerCase().includes(q) ||
      (tc.key ?? '').toLowerCase().includes(q)
    );
  }, [cases, search]);

  const filterApplied = useMemo(() => {
    if (activeFilters.length === 0) return filtered;
    const grouped = activeFilters.reduce((acc, { field, value }) => {
      (acc[field] = acc[field] || []).push(value.toLowerCase());
      return acc;
    }, {});
    return filtered.filter(tc =>
      Object.entries(grouped).every(([field, values]) => {
        const raw = tc[field];
        const isMissing = Array.isArray(raw) ? raw.length === 0 : (raw == null || raw === '');
        if (values.includes(MISSING_VALUE)) {
          if (isMissing) return true;
          const nonMissingValues = values.filter(v => v !== MISSING_VALUE);
          if (nonMissingValues.length === 0) return false;
          if (Array.isArray(raw)) return raw.some(v => nonMissingValues.includes(v.toString().toLowerCase()));
          return nonMissingValues.includes(raw.toString().toLowerCase());
        }
        if (Array.isArray(raw)) return raw.some(v => values.includes(v.toString().toLowerCase()));
        return values.includes((raw ?? '').toString().toLowerCase());
      })
    );
  }, [filtered, activeFilters]);

  const fieldOptions = useMemo(() => {
    const result = {};
    FILTERABLE_FIELDS.forEach(({ key }) => {
      const hasMissing = cases.some(tc => {
        const v = tc[key];
        return Array.isArray(v) ? v.length === 0 : (v == null || v === '');
      });
      const vals = [...new Set(
        cases.flatMap(tc => {
          const v = tc[key];
          return Array.isArray(v) ? v : (v != null && v !== '' ? [v] : []);
        })
      )].sort((a, b) => a.toString().localeCompare(b.toString()));
      if (hasMissing) vals.push(MISSING_VALUE);
      result[key] = vals;
    });
    return result;
  }, [cases]);

  const sorted = useMemo(() => {
    if (!sortCol) return filterApplied;
    return [...filterApplied].sort((a, b) => {
      const av = (a[sortCol] ?? '').toString().toLowerCase();
      const bv = (b[sortCol] ?? '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filterApplied, sortCol, sortDir]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  // Select-all operates over the whole filtered/sorted set, not just the current page.
  const allKeys = useMemo(() => sorted.map(tc => tc.key), [sorted]);
  const allSelected = allKeys.length > 0 && allKeys.every(k => selectedKeys.has(k));
  const someSelected = allKeys.some(k => selectedKeys.has(k));

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  function toggleAll() {
    setSelectedKeys(prev => {
      if (allKeys.length > 0 && allKeys.every(k => prev.has(k))) {
        const next = new Set(prev);
        allKeys.forEach(k => next.delete(k));
        return next;
      }
      return new Set([...prev, ...allKeys]);
    });
  }

  // Set a single-select / text / rich-text field value.
  function setBulkValue(key, value) {
    setBulkValues(prev => ({ ...prev, [key]: value }));
  }

  // Multi-select fields (projectType / techStack) store a string[]; set the whole list.
  function setMultiValues(key, values) {
    setBulkValues(prev => ({ ...prev, [key]: values }));
  }

  // Labels are { operation, values }; default operation is ADD.
  function setLabelsOperation(operation) {
    setBulkValues(prev => ({ ...prev, labels: { operation, values: prev.labels?.values ?? [] } }));
  }
  function setLabelValues(values) {
    setBulkValues(prev => ({ ...prev, labels: { operation: prev.labels?.operation ?? 'ADD', values } }));
  }

  // Build the `updates` payload from the chosen values, one entry per field the
  // user actually set. An untouched field is omitted (i.e. "keep").
  function buildBulkUpdates() {
    const updates = {};
    BULK_FIELD_GROUPS.forEach(group => {
      group.fields.forEach(({ key }) => {
        const v = bulkValues[key];
        switch (group.kind) {
          case 'single':
          case 'text':
          case 'rich':
            if (typeof v === 'string' && v.trim() !== '') updates[key] = v;
            break;
          case 'multi':
            if (Array.isArray(v) && v.length > 0) updates[key] = v;
            break;
          case 'labels':
            if (v && Array.isArray(v.values) && v.values.length > 0) {
              updates[key] = { operation: v.operation ?? 'ADD', values: v.values };
            }
            break;
          default:
            break;
        }
      });
    });
    return updates;
  }

  const chosenUpdates = buildBulkUpdates();
  const hasChosenValue = Object.keys(chosenUpdates).length > 0;

  async function applyBulkUpdate() {
    const updates = buildBulkUpdates();
    const keys = [...selectedKeys];
    if (!keys.length || Object.keys(updates).length === 0 || !onBulkUpdate) return;
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      const result = await onBulkUpdate({ project: selectedProject, keys, updates });
      setBulkResult(result);
      setSelectedKeys(new Set());
      setBulkValues({});
    } catch (e) {
      setBulkResult({ error: e?.response?.data?.message || 'Bulk update failed.' });
    } finally {
      setBulkSubmitting(false);
    }
  }

  return (
    <div className="tm-body">
      <div className={`tm-folders-bar ${foldersCollapsed ? 'tm-collapsed' : ''}`}>
        {!selectedProject && (
          <p className="tm-folders-bar-empty">Select a project to view folders.</p>
        )}
        {selectedProject && folderTree && (
          <>
            <label
              className={`tm-subfolders-toggle ${recursive ? 'active' : ''}`}
              title={recursive ? 'Showing test cases from folder and all subfolders' : 'Showing test cases from selected folder only'}
              onClick={onToggleRecursive}
            >
              <Layers size={13} />
              <span>Include subfolders tests</span>
              <span className={`tm-subfolders-switch ${recursive ? 'on' : ''}`}>
                <span className="tm-subfolders-thumb" />
              </span>
            </label>
            <div className="tm-folder-root-row">
              <button
                className={`tm-folder-root-item ${!selectedFolder ? 'active' : ''}`}
                onClick={() => onSelectFolder(null)}
              >
                <FolderOpen size={14} />
                <span className="tm-folder-item-name">All</span>
                <span className="tm-folder-count">{folderTree.testCaseCount}</span>
              </button>
              <AddFolderControl parentNode={null} onCreateFolder={onCreateFolder} title="Add root folder" />
            </div>
            <div className="tm-folders-list">
              {folderTree.children?.map(child => (
                <FolderNode
                  key={child.id}
                  node={child}
                  depth={0}
                  selectedFolder={selectedFolder}
                  expandedIds={expandedIds}
                  onSelectFolder={onSelectFolder}
                  onDeleteFolder={onDeleteFolder}
                  onRenameFolder={onRenameFolder}
                  onCreateFolder={onCreateFolder}
                />
              ))}
            </div>
          </>
        )}
        {selectedProject && !folderTree && (
          <p className="tm-folders-bar-empty">Loading folders…</p>
        )}
      </div>

      <div className={`tm-right ${bulkMode ? 'tm-panel-open' : ''}`}>
        <div className="tm-toolbar">
          <div className="tm-toolbar-left">
            <button
              className="tm-filter-btn tm-folders-toggle"
              onClick={() => setFoldersCollapsed(c => !c)}
              title={foldersCollapsed ? 'Show folder tree' : 'Hide folder tree'}
              aria-label={foldersCollapsed ? 'Show folder tree' : 'Hide folder tree'}
            >
              {foldersCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
            <button
              className="tm-add-btn-toolbar"
              onClick={handleAddTest}
              title="Create a new test case in Jira"
            >
              <Plus size={15} />
              Add new test
            </button>
            <div className="tm-search-wrap">
              <Search size={15} className="tm-search-icon" />
              <input
                type="text"
                placeholder="Search by name, key (MOBILE-123), number (123), or Jira URL…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="tm-search-input"
              />
            </div>
          </div>
          <div className="tm-toolbar-right">
            <button
              className={`tm-filter-btn ${bulkMode ? 'active' : ''}`}
              onClick={() => (bulkMode ? exitBulkMode() : enterBulkMode())}
              disabled={!selectedProject}
              title="Select test cases to bulk update"
            >
              <ListChecks size={16} />
              Select
            </button>
            <button
              className={`tm-filter-btn ${filterOpen ? 'active' : ''}`}
              onClick={() => { setFilterOpen(o => !o); setColPickerOpen(false); setFilterMenuOpen(false); setFilterMenuStep('field'); setFilterMenuField(null); }}
              title="Filters"
            >
              <ListFilter size={16} />
              Filters
              {activeFilters.length > 0 && <span className="tm-filter-badge">{activeFilters.length}</span>}
            </button>
            <div className="tm-col-picker-wrap">
              <button
                className={`tm-filter-btn ${colPickerOpen ? 'active' : ''}`}
                onClick={() => { setColPickerOpen(o => !o); setFilterOpen(false); }}
                title="Columns"
              >
                <Columns3 size={16} />
              </button>
              {colPickerOpen && (
                <div className="tm-col-picker-dropdown">
                  {COLUMNS.map(col => (
                    <label key={col.key} className="tm-col-picker-item">
                      <input
                        type="checkbox"
                        checked={visibleCols.has(col.key)}
                        onChange={() => {
                          setVisibleCols(prev => {
                            const next = new Set(prev);
                            next.has(col.key) ? next.delete(col.key) : next.add(col.key);
                            return next;
                          });
                        }}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="tm-content">
          {!selectedProject && (
            <p className="tm-state-msg">Select a project to view test cases.</p>
          )}
          {selectedProject && loading && <p className="tm-state-msg">Loading…</p>}
          {selectedProject && error && <p className="tm-state-msg tm-error">{error}</p>}

          {selectedProject && !loading && !error && (
            <>
              {filterOpen && (
                <div className="tm-filter-panel">
                  {activeFilters.map((f, i) => {
                    const fieldLabel = FILTERABLE_FIELDS.find(ff => ff.key === f.field)?.label ?? f.field;
                    return (
                      <span key={`${f.field}:${f.value}`} className="tm-filter-chip">
                        {fieldLabel}: {f.value === MISSING_VALUE ? <Missing /> : f.value}
                        <button
                          className="tm-filter-chip-remove"
                          onClick={() => setActiveFilters(prev => prev.filter((_, idx) => idx !== i))}
                          aria-label={`Remove ${fieldLabel}: ${f.value} filter`}
                        >×</button>
                      </span>
                    );
                  })}
                  <div className="tm-filter-add-wrap" ref={filterMenuRef}>
                    <button
                      className="tm-filter-add-btn"
                      onClick={() => {
                        setFilterMenuOpen(o => !o);
                        setFilterMenuStep('field');
                        setFilterMenuField(null);
                      }}
                    >
                      + Add filter
                    </button>
                    {filterMenuOpen && (
                      <div className="tm-filter-dropdown">
                        {filterMenuStep === 'field' && (
                          <>
                            <div className="tm-filter-dropdown-header">Filter by</div>
                            {FILTERABLE_FIELDS.map(ff => (
                              <button
                                key={ff.key}
                                className="tm-filter-dropdown-item"
                                onClick={() => {
                                  setFilterMenuField(ff.key);
                                  setFilterMenuStep('value');
                                }}
                              >
                                {ff.label}
                              </button>
                            ))}
                          </>
                        )}
                        {filterMenuStep === 'value' && filterMenuField && (
                          <>
                            <button
                              className="tm-filter-dropdown-back"
                              onClick={() => { setFilterMenuStep('field'); setFilterMenuField(null); }}
                            >
                              ← {FILTERABLE_FIELDS.find(ff => ff.key === filterMenuField)?.label}
                            </button>
                            {(fieldOptions[filterMenuField] ?? []).map(val => (
                              <button
                                key={val}
                                className="tm-filter-dropdown-item"
                                onClick={() => {
                                  const alreadyAdded = activeFilters.some(
                                    f => f.field === filterMenuField && f.value === String(val)
                                  );
                                  if (!alreadyAdded) {
                                    setActiveFilters(prev => [...prev, { field: filterMenuField, value: String(val) }]);
                                  }
                                  setFilterMenuOpen(false);
                                  setFilterMenuStep('field');
                                  setFilterMenuField(null);
                                }}
                              >
                                {val === MISSING_VALUE ? <Missing /> : String(val)}
                              </button>
                            ))}
                            {(fieldOptions[filterMenuField] ?? []).length === 0 && (
                              <span className="tm-filter-dropdown-empty">No values</span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {activeFilters.length > 0 && (
                    <>
                      <div className="tm-filter-spacer" />
                      <button
                        className="tm-filter-clear-btn"
                        onClick={() => setActiveFilters([])}
                      >
                        Clear all
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="tm-table-wrap">
                <table className="tm-table">
                  <thead>
                    <tr>
                      {bulkMode && (
                        <th className="tm-bulk-check-col">
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            aria-label="Select all test cases"
                          />
                        </th>
                      )}
                      {COLUMNS.filter(col => visibleCols.has(col.key)).map(col => (
                        <th key={col.key} onClick={() => handleSort(col.key)} className="sortable-th">
                          {col.label}
                          <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(tc => (
                      <tr
                        key={tc.key}
                        onClick={() => (bulkMode ? toggleKey(tc.key) : onCaseClick(tc.id, tc.key))}
                        className={`tm-row-clickable${bulkMode && selectedKeys.has(tc.key) ? ' tm-row-selected' : ''}`}
                      >
                        {bulkMode && (
                          <td className="tm-bulk-check-col" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(tc.key)}
                              onChange={() => toggleKey(tc.key)}
                              aria-label={`Select ${tc.key}`}
                            />
                          </td>
                        )}
                        {visibleCols.has('key') && <td>
                          <a href={`${JIRA_BASE}/${tc.key}`} target="_blank" rel="noopener noreferrer" className="tm-key-link">
                            <ExternalLink size={13} />
                            {tc.key}
                          </a>
                        </td>}
                        {visibleCols.has('summary') && <td><span className="tm-summary-cell"><Expand size={13} className="tm-expand-icon" />{tc.summary || '—'}</span></td>}
                        {visibleCols.has('type') && <td>{tc.type || '—'}</td>}
                        {visibleCols.has('reporter') && <td>{tc.reporter || '—'}</td>}
                        {visibleCols.has('created') && <td className="tm-date">{formatDate(tc.created)}</td>}
                        {visibleCols.has('automationStatus') && <td>{tc.automationStatus || '—'}</td>}
                        {visibleCols.has('unit') && <td>{tc.unit || '—'}</td>}
                        {visibleCols.has('status') && <td>
                          <span className={`tm-status-chip ${statusClass(tc.status)}`}>
                            {tc.status || '—'}
                          </span>
                        </td>}
                        {visibleCols.has('testPyramid') && <td>{tc.testPyramid || '—'}</td>}
                        {visibleCols.has('parentId') && <td>{tc.parentId ?? '—'}</td>}
                        {visibleCols.has('mobile_framework') && <td><FrameworkPills values={tc.mobile_framework} /></td>}
                        {visibleCols.has('mobile_os') && <td><OsPills values={tc.mobile_os} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="tm-pagination">
                <div className="tm-pagination-left">
                  <span className="tm-pagination-info">
                    {sorted.length === 0
                      ? 'No test cases'
                      : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, sorted.length)} of ${
                          search.trim() || activeFilters.length > 0 ? sorted.length : (totalApiCases || sorted.length)
                        }${search.trim() || activeFilters.length > 0 ? ` (filtered from ${totalApiCases || cases.length})` : ''} test cases`}
                  </span>
                </div>
                <div className="tm-pagination-right">
                  <button className="tm-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                  <button
                    className="tm-page-btn"
                    disabled={(page >= totalPages && cases.length >= (totalApiCases || cases.length)) || loadingMore}
                    onClick={async () => {
                      if (page < totalPages) {
                        setPage(p => p + 1);
                      } else if (cases.length < totalApiCases && onLoadMore) {
                        await onLoadMore();
                        setPage(p => p + 1);
                      }
                    }}
                  >›</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {bulkMode && (
        <div className="tm-bulk-panel" role="dialog" aria-label="Bulk edit test cases">
          <div className="tm-bulk-panel-header">
            <div className="tm-bulk-panel-title">
              Bulk edit — <strong>{selectedKeys.size}</strong> selected
            </div>
            <button
              className="tm-bulk-panel-close"
              onClick={exitBulkMode}
              disabled={bulkSubmitting}
              aria-label="Close bulk edit"
            >
              <X size={18} />
            </button>
          </div>

          <div className="tm-bulk-panel-body">
            {fieldOptsLoading && <p className="tm-bulk-bar-msg">Loading field options…</p>}
            {fieldOptsError && <p className="tm-bulk-bar-msg tm-bulk-bar-err">{fieldOptsError}</p>}
            {!fieldOptsLoading && !fieldOptsError && (
              <div className="tm-bulk-panel-groups">
                {BULK_FIELD_GROUPS.map(group => (
                  <section key={group.key} className="tm-bulk-group">
                    <div className="tm-bulk-group-title">
                      {group.title}
                      <span className="tm-bulk-group-hint">{group.hint}</span>
                    </div>

                    {group.fields.map(({ key, label }) => {
                      if (group.kind === 'single') {
                        const opts = fieldOpts?.[key] ?? [];
                        return (
                          <label key={key} className="tm-bulk-field">
                            <span className="tm-bulk-field-label">{label}</span>
                            <select
                              className="tm-bulk-select"
                              value={bulkValues[key] ?? ''}
                              disabled={opts.length === 0 || bulkSubmitting}
                              onChange={e => setBulkValue(key, e.target.value)}
                            >
                              <option value="">— keep —</option>
                              {opts.map(o => (
                                <option key={o.id} value={o.value}>{o.value}</option>
                              ))}
                            </select>
                          </label>
                        );
                      }

                      if (group.kind === 'multi') {
                        const opts = (fieldOpts?.[key] ?? []).map(o => o.value);
                        const selected = Array.isArray(bulkValues[key]) ? bulkValues[key] : [];
                        return (
                          <div key={key} className="tm-bulk-field">
                            <span className="tm-bulk-field-label">{label}</span>
                            <BulkMultiSelect
                              options={opts}
                              selected={selected}
                              disabled={bulkSubmitting || opts.length === 0}
                              placeholder={opts.length ? 'Select…' : 'No values'}
                              onChange={vals => setMultiValues(key, vals)}
                            />
                          </div>
                        );
                      }

                      if (group.kind === 'labels') {
                        const opts = fieldOpts?.labels ?? [];
                        const operation = bulkValues.labels?.operation ?? 'ADD';
                        const selected = bulkValues.labels?.values ?? [];
                        return (
                          <div key={key} className="tm-bulk-field">
                            <span className="tm-bulk-field-label">{label}</span>
                            <div className="tm-bulk-op-seg">
                              {LABELS_OPERATIONS.map(op => (
                                <button
                                  key={op}
                                  type="button"
                                  className={operation === op ? 'active' : ''}
                                  disabled={bulkSubmitting}
                                  onClick={() => setLabelsOperation(op)}
                                >
                                  {op}
                                </button>
                              ))}
                            </div>
                            <BulkMultiSelect
                              options={opts}
                              selected={selected}
                              disabled={bulkSubmitting || opts.length === 0}
                              placeholder={opts.length ? 'Select labels…' : 'No project labels'}
                              onChange={setLabelValues}
                            />
                          </div>
                        );
                      }

                      if (group.kind === 'text') {
                        // Folder path uses the same tree picker as single-test edit;
                        // its onPathChange emits the slash-separated path (project excluded).
                        if (key === 'folderPath') {
                          return (
                            <div key={key} className="tm-bulk-field">
                              <span className="tm-bulk-field-label">{label}</span>
                              <FolderPicker
                                rootChildren={folderTree?.children ?? []}
                                onPathChange={path => setBulkValue('folderPath', path)}
                              />
                            </div>
                          );
                        }
                        return (
                          <label key={key} className="tm-bulk-field">
                            <span className="tm-bulk-field-label">{label}</span>
                            <input
                              type="text"
                              className="tm-bulk-text-input"
                              placeholder="— keep —"
                              value={bulkValues[key] ?? ''}
                              disabled={bulkSubmitting}
                              onChange={e => setBulkValue(key, e.target.value)}
                            />
                          </label>
                        );
                      }

                      if (group.kind === 'rich') {
                        return (
                          <label key={key} className="tm-bulk-field">
                            <span className="tm-bulk-field-label">{label}</span>
                            <textarea
                              className="tm-bulk-textarea"
                              placeholder="— keep —"
                              value={bulkValues[key] ?? ''}
                              disabled={bulkSubmitting}
                              onChange={e => setBulkValue(key, e.target.value)}
                            />
                          </label>
                        );
                      }

                      return null;
                    })}
                  </section>
                ))}
              </div>
            )}
          </div>

          <div className="tm-bulk-panel-footer">
            {bulkResult && (
              <div className="tm-bulk-result">
                {bulkResult.error ? (
                  <span className="tm-bulk-bar-err">{bulkResult.error}</span>
                ) : (
                  <>
                    <span className="tm-bulk-ok">{bulkResult.updated?.length ?? 0} updated</span>
                    {bulkResult.failed?.length > 0 && (
                      <span
                        className="tm-bulk-bar-err"
                        title={bulkResult.failed.map(f => `${f.key}: ${f.error}`).join('\n')}
                      >
                        {bulkResult.failed.length} failed
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="tm-bulk-bar-actions">
              <button className="tm-bulk-cancel" onClick={exitBulkMode} disabled={bulkSubmitting}>
                Cancel
              </button>
              <button
                className="tm-bulk-apply"
                onClick={applyBulkUpdate}
                disabled={bulkSubmitting || selectedKeys.size === 0 || !hasChosenValue}
              >
                {bulkSubmitting
                  ? 'Updating…'
                  : `Update ${selectedKeys.size} test${selectedKeys.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestDesign;
